import { createQueryClient } from "@khazzin/api-client/query";
import { endSession } from "@khazzin/auth";
import { QueryClientProvider } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { useMemo } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router";
import { APP_VERSION, ApiClientProvider, useApi, useUpgradeRequired } from "./api";
import { HeldWork, Home } from "./app/home/Home";
import { Cash } from "./app/shell/Cash";
import { Dashboard } from "./app/shell/Dashboard";
import { History, HistoryDetail } from "./app/shell/History";
import { DriverInbox } from "./app/shell/Inbox";
import { Profile } from "./app/shell/Profile";
import { Shell } from "./app/shell/Shell";
import { StopDetail } from "./app/shell/StopDetail";
import { Today } from "./app/shell/Today";
import { UpgradeGate } from "./app/UpgradeGate";
import { I18nProvider } from "./i18n";
import { HandoverSummary } from "./modules/cash/HandoverSummary";
import { OfflineProvider, useOffline } from "./offline/Offline";

/**
 * The Driver App (EP10-S15): providers, the offline state and the tabs.
 *
 * Signed in, the app runs on the cached day and syncs. Signed out with a
 * usable cache and no signal — the session cannot refresh in a village — it
 * still opens on the cache, and the outbox waits for the next sign-in. Signed
 * out otherwise, it is the sign-in screen.
 */

function Routes() {
	const { ready, day, online, held } = useOffline();
	const { user } = useAuth();
	const upgrade = useUpgradeRequired();
	const router = useMemo(
		() =>
			createBrowserRouter([
				{
					path: "/",
					element: <Shell />,
					children: [
						// ADJ-1022-8b: the app lands on the Dashboard, on shift or not.
						{ index: true, element: <Navigate to="/home" replace /> },
						{ path: "home", element: <Dashboard /> },
						{ path: "today", element: <Today /> },
						{ path: "history", element: <History /> },
						{ path: "history/:shipmentId", element: <HistoryDetail /> },
						{ path: "today/:shipmentId", element: <StopDetail /> },
						{ path: "cash", element: <Cash /> },
						{ path: "cash/handover", element: <HandoverSummary /> },
						{ path: "profile", element: <Profile appVersion={APP_VERSION} /> },
						// EP12-S15: the bell's "View all".
						{ path: "inbox", element: <DriverInbox /> },
						{ path: "*", element: <Navigate to="/today" replace /> },
					],
				},
			]),
		[],
	);
	if (!ready) {
		return null;
	}
	if (user === null && (online || day === null)) {
		return <Home />;
	}
	// ADJ-M03-4: another driver's unsynced work is on this phone.
	if (held !== null) {
		return <HeldWork work={held} />;
	}
	// EP10-S23: the server refused this build. Nothing else is reachable, and the
	// sync loop, which lives above this, keeps draining the queue.
	if (upgrade) {
		return <UpgradeGate />;
	}
	return <RouterProvider router={router} />;
}

function Session() {
	const { user, isLoading, signOut } = useAuth();
	const api = useApi();
	if (isLoading) {
		return null;
	}
	return (
		<OfflineProvider
			userId={user?.id ?? null}
			// ADJ-M03-4: our session ends too, not only AuthKit's.
			onSignedOut={() => endSession(api, signOut)}
			appVersion={APP_VERSION}
		>
			<Routes />
		</OfflineProvider>
	);
}

export function App() {
	const queryClient = useMemo(createQueryClient, []);
	return (
		<QueryClientProvider client={queryClient}>
			<ApiClientProvider>
				<I18nProvider>
					<Session />
				</I18nProvider>
			</ApiClientProvider>
		</QueryClientProvider>
	);
}
